import { useState, useEffect } from 'react'
import { useAccount, useContractRead, useNetwork, useDisconnect } from 'wagmi'
import { ADDRESSES, CONFIG, CHAIN_ID } from '../config'
import { motion } from 'framer-motion'
import { Abi } from 'viem'
import { Trash, FloppyDisk, Lock, SignOut } from '@phosphor-icons/react'
import { useAdmin } from '../hooks/useAdmin'
import { useContractAddresses } from '../hooks/useContractAddresses'
import { useNavigate } from 'react-router-dom'

export function AdminPanel() {
  const { address } = useAccount()
  const { chain } = useNetwork()
  const { disconnect } = useDisconnect()
  const navigate = useNavigate()

  const handleLogout = () => {
    localStorage.removeItem('trinity_auth')
    localStorage.removeItem('trinity_is_admin')
    disconnect()
    navigate('/')
  }

  // Admin settings
  const [adminPassword, setAdminPassword] = useState('')
  const [geoEnabled, setGeoEnabled] = useState(true)
  const [allowedCountries, setAllowedCountries] = useState<string[]>([
    'US', 'GB', 'CA', 'AU', 'NZ', 'SG', 'AE', 'CH', 'DE', 'FR', 'NL', 'JP', 'KR'
  ])
  const [newCountry, setNewCountry] = useState('')
  const { adminEnabled, setAdminEnabled } = useAdmin()

  // Contract address management
  const { contractAddresses, setContractAddresses, saveAddresses } = useContractAddresses();

  const { data: ownerAddress } = useContractRead({
    address: ADDRESSES.TRINITY_MASTER,
    abi: CONFIG.TRINITY_ABI.abi as Abi,
    functionName: 'owner',
    enabled: chain?.id === CHAIN_ID,
  })

  // Load saved settings
  useEffect(() => {
    const savedPassword = localStorage.getItem('trinity_admin_password')
    if (savedPassword) {
      setAdminPassword(savedPassword)
    }

    const savedGeoConfig = localStorage.getItem('trinity_geo_config')
    if (savedGeoConfig) {
      const config = JSON.parse(savedGeoConfig)
      setGeoEnabled(config.enabled)
      setAllowedCountries(config.allowedCountries)
    }
  }, [])

  // Save settings
  const saveAdminPassword = () => {
    if (adminPassword) {
      localStorage.setItem('trinity_admin_password', adminPassword)
    }
  }

  const saveGeoConfig = () => {
    const config = {
      enabled: geoEnabled,
      allowedCountries
    }
    localStorage.setItem('trinity_geo_config', JSON.stringify(config))
  }

  const addCountry = () => {
    if (newCountry && !allowedCountries.includes(newCountry)) {
      setAllowedCountries([...allowedCountries, newCountry.toUpperCase()])
      setNewCountry('')
      saveGeoConfig()
    }
  }

  const removeCountry = (country: string) => {
    setAllowedCountries(allowedCountries.filter(c => c !== country))
    saveGeoConfig()
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-4xl font-bold mb-3 bg-gradient-to-r from-blue-500/90 via-purple-500/90 to-pink-500/90 text-transparent bg-clip-text">Admin Panel</h1>
      <p className="text-base text-secondary"></p>
      <div className="space-y-8">
        {/* Contract Address Management */}
        <section className="space-y-4">
          <h3 className="text-xl font-semibold">Contract Addresses</h3>
          
          <motion.div
            className="glass-panel p-6 rounded-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="space-y-4">
              {Object.entries(contractAddresses).map(([key, value]) => (
                <div key={key} className="grid grid-cols-[200px,1fr] gap-4 rounded-2xl items-center">
                  <label className="text-sm font-medium text-gray-300 capitalize">
                    {key.replace(/([A-Z])/g, ' $1').trim()}:
                  </label>
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => setContractAddresses(prev => ({
                      ...prev,
                      [key]: e.target.value as `0x${string}`
                    }))}
                    className="flex-1 px-4 py-2 bg-black/40 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 font-mono text-sm"
                    placeholder={`Enter ${key} address`}
                  />
                </div>
              ))}
              
              <div className="flex justify-end mt-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={saveAddresses}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded transition-colors text-blue-400"
                >
                  <FloppyDisk size={20} />
                  Save Addresses
                </motion.button>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Admin Settings */}
        <section className="space-y-4">
          <h3 className="text-xl font-semibold">Admin Settings</h3>
          
          <motion.div
            className="glass-panel p-6 rounded-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="space-y-6">
              {/* Admin Mode Toggle and Logout */}
              <div>
                <h4 className="font-medium mb-4">Admin Mode</h4>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setAdminEnabled(!adminEnabled)}
                    className={`px-4 py-2 rounded transition-colors ${
                      adminEnabled 
                        ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30' 
                        : 'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Lock size={20} />
                      {adminEnabled ? 'Disable Admin Mode' : 'Enable Admin Mode'}
                    </div>
                  </button>
                  
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <SignOut size={20} />
                      Logout
                    </div>
                  </button>
                </div>
              </div>

              {/* Admin Password */}
              <div>
                <h4 className="font-medium mb-4">Admin Password</h4>
                <div className="flex gap-2">
                  <input
                    type="password"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    placeholder="Set admin password"
                    className="flex-1 px-4 py-2 bg-black/40 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  />
                  <button
                    onClick={saveAdminPassword}
                    className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded transition-colors"
                  >
                    Save Password
                  </button>
                </div>
              </div>

              {/* Geo Configuration */}
              <div>
                <h4 className="font-medium mb-4">Geo Configuration</h4>
                <div className="space-y-4">
                  {/* Enable/Disable Geo */}
                  <div className="flex items-center gap-4">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={geoEnabled}
                        onChange={(e) => {
                          setGeoEnabled(e.target.checked)
                          saveGeoConfig()
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                    <span className="text-sm font-medium text-gray-300">
                      {geoEnabled ? 'Geo-blocking Enabled' : 'Geo-blocking Disabled'}
                    </span>
                  </div>

                  {/* Country Management */}
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newCountry}
                        onChange={(e) => setNewCountry(e.target.value.toUpperCase())}
                        placeholder="Country code (e.g. US)"
                        maxLength={2}
                        className="flex-1 px-4 py-2 bg-black/40 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      />
                      <button
                        onClick={addCountry}
                        disabled={!newCountry || newCountry.length !== 2}
                        className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded transition-colors disabled:opacity-50"
                      >
                        Add Country
                      </button>
                    </div>

                    <div className="max-h-40 overflow-y-auto space-y-2">
                      {allowedCountries.map((country) => (
                        <div
                          key={country}
                          className="flex items-center justify-between p-2 bg-gray-800/50 rounded"
                        >
                          <span>{country}</span>
                          <button
                            onClick={() => removeCountry(country)}
                            className="text-red-500 hover:text-red-400"
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </section>
      </div>
    </div>
  )
} 